import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ExternalLink } from 'lucide-react';

const navLinks = [
  { name: 'Accueil', path: '/' },
  { name: 'Menuiserie', path: '/menuiserie' },
  { name: 'Ossature Bois', path: '/ossature-bois' },
  { name: 'Isolation', path: '/isolation' },
  { name: 'Contact', path: '/contact' },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <header 
      data-testid="header"
      className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#0a0908]/80 border-b border-[#C5A059]/10"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex items-center h-20">
          {/* Logo - Left */}
          <Link to="/" data-testid="header-logo" className="flex items-center flex-shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_menuiserie-doree/artifacts/r267ou8u_1.png" 
              alt="Rozier Menuiserie" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation - Center */}
          <nav className="hidden lg:flex items-center justify-center gap-8 flex-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-sm uppercase tracking-[0.15em] font-body transition-colors duration-300 ${
                  location.pathname === link.path 
                    ? 'text-[#C5A059]' 
                    : 'text-[#a39c93] hover:text-[#fdfbf7]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button - Right */}
          <div className="hidden lg:flex items-center flex-shrink-0">
            <a
              href="https://rozier-concept.fr"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-rozier-concept"
              className="flex items-center gap-2 bg-transparent border border-[#C5A059] text-[#C5A059] px-6 py-3 text-xs uppercase tracking-[0.15em] hover:bg-[#C5A059]/10 transition-colors duration-300"
            >
              Rozier Concept
              <ExternalLink size={14} />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-[#fdfbf7] p-2 ml-auto"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#0a0908]/95 backdrop-blur-xl border-t border-[#C5A059]/10"
            data-testid="mobile-menu"
          >
            <nav className="px-6 py-8 flex flex-col gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-lg font-heading transition-colors duration-300 ${
                    location.pathname === link.path 
                      ? 'text-[#C5A059]' 
                      : 'text-[#a39c93] hover:text-[#fdfbf7]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <a
                href="https://rozier-concept.fr"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-cta-rozier-concept"
                className="flex items-center gap-2 text-[#C5A059] text-lg font-heading mt-4"
                onClick={() => setMobileMenuOpen(false)}
              >
                Rozier Concept
                <ExternalLink size={18} />
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
